/**
 * MonthDetail — full view of one month's training summary at /months/:iso.
 *
 * Renders:
 *   1. Header (date range + Back / Regenerate)
 *   2. Stats panel (totals, CTL/ATL/TSB endpoints, CTL trajectory line)
 *   3. Month-level safety flag pills (if any fired)
 *   4. Per-week mini-cards — gives the visual arc of the month
 *   5. Coach analysis (three-section markdown parsed into headers)
 *   6. Vs-last-month comparison table (TSS / hours / km / TSB end)
 *
 * Empty months render "Rest month — nothing to analyze" without a
 * generate button. Missing-summary case shows a generate button in
 * place of the analysis text. Invalid iso (not first-of-month) gets
 * an explicit error page with a back link.
 */
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useGenerateMonthlySummary,
  useMonthlySummary,
} from '../hooks/useMonthlySummary.ts';
import { GarminAiAttribution, RideAttribution } from '../components/GarminAttribution.tsx';
import { SafetyFlagPill } from '../components/SafetyFlagPill.tsx';
import type {
  MonthlyStats,
  MonthlySummary,
  MonthlyWeekBlock,
} from '../types/api.ts';

function isoFirstOfMonth(iso: string | undefined): string | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.getUTCDate() === 1 ? iso : null;
}

function prevMonthIso(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 10);
}

function formatDateRange(start: string, end: string): string {
  const fmt = (s: string) =>
    new Date(`${s}T00:00:00Z`).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  return `${fmt(start)} – ${fmt(end)}`;
}

function monthLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

interface ParsedSection {
  heading: string;
  body: string;
}

function parseSummarySections(summary: string): ParsedSection[] {
  const pattern = /^##[ \t]+(\S.*)$/gm;
  const headers: Array<{ heading: string; start: number; bodyStart: number }> = [];
  let match;
  while ((match = pattern.exec(summary)) !== null) {
    headers.push({
      heading: match[1].trim(),
      start: match.index,
      bodyStart: match.index + match[0].length,
    });
  }
  if (headers.length === 0) {
    return [{ heading: '', body: summary.trim() }];
  }
  return headers.map((h, i) => {
    const end = i + 1 < headers.length ? headers[i + 1].start : summary.length;
    return { heading: h.heading, body: summary.slice(h.bodyStart, end).trim() };
  });
}

interface DeltaCell {
  label: string;
  cur: number;
  prev: number;
  fmtCur: string;
  fmtPrev: string;
  fmtDelta: string;
}

function buildComparison(
  cur: MonthlyStats,
  prev: MonthlyStats | undefined,
): DeltaCell[] | null {
  if (!prev) return null;
  const fmt = (n: number, suffix: string) =>
    `${Number.isInteger(n) ? n : n.toFixed(1)}${suffix}`;
  const fmtDelta = (d: number, suffix: string) => {
    const rounded = Math.abs(d) >= 10 ? Math.round(d) : Number(d.toFixed(1));
    const sign = rounded >= 0 ? '+' : '';
    return `${sign}${rounded}${suffix}`;
  };
  return [
    {
      label: 'TSS',
      cur: cur.totals.tss,
      prev: prev.totals.tss,
      fmtCur: fmt(cur.totals.tss, ''),
      fmtPrev: fmt(prev.totals.tss, ''),
      fmtDelta: fmtDelta(cur.totals.tss - prev.totals.tss, ''),
    },
    {
      label: 'Hours',
      cur: cur.totals.hours,
      prev: prev.totals.hours,
      fmtCur: fmt(cur.totals.hours, 'h'),
      fmtPrev: fmt(prev.totals.hours, 'h'),
      fmtDelta: fmtDelta(cur.totals.hours - prev.totals.hours, 'h'),
    },
    {
      label: 'Distance',
      cur: cur.totals.km,
      prev: prev.totals.km,
      fmtCur: fmt(cur.totals.km, ' km'),
      fmtPrev: fmt(prev.totals.km, ' km'),
      fmtDelta: fmtDelta(cur.totals.km - prev.totals.km, ' km'),
    },
    {
      label: 'TSB end',
      cur: cur.tsb_end,
      prev: prev.tsb_end,
      fmtCur: fmt(cur.tsb_end, ''),
      fmtPrev: fmt(prev.tsb_end, ''),
      fmtDelta: fmtDelta(cur.tsb_end - prev.tsb_end, ''),
    },
  ];
}

export function MonthDetail() {
  const { t } = useTranslation();
  const { iso } = useParams<{ iso: string }>();
  const monthIso = useMemo(() => isoFirstOfMonth(iso), [iso]);
  const prevIso = useMemo(
    () => (monthIso ? prevMonthIso(monthIso) : null),
    [monthIso],
  );

  const cur = useMonthlySummary(monthIso);
  const prev = useMonthlySummary(prevIso);
  const generate = useGenerateMonthlySummary();

  if (!monthIso) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-lg font-semibold text-(--color-text-primary)">
          {t('monthDetail.invalidIso')}
        </h1>
        <Link
          to="/rides"
          className="text-emerald-400 hover:text-emerald-300 text-sm mt-2 inline-block"
        >
          ← {t('monthDetail.backToCalendar')}
        </Link>
      </div>
    );
  }

  if (cur.isLoading || !cur.data) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="h-6 w-48 rounded-lg bg-(--color-bg-tertiary) animate-pulse" />
        <div className="h-24 mt-4 rounded-xl bg-(--color-bg-tertiary) animate-pulse" />
      </div>
    );
  }

  const data: MonthlySummary = cur.data;
  const stats = data.stats;
  const isEmpty = stats.totals.rides === 0;
  const sections = data.summary ? parseSummarySections(data.summary) : null;
  const comparison = isEmpty ? null : buildComparison(stats, prev.data?.stats);
  const isGenerating =
    generate.isPending && generate.variables === monthIso;
  const generateLabel = data.summary
    ? t('monthDetail.regenerate')
    : t('monthDetail.generate');

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-1 min-w-0">
          <Link
            to="/rides"
            className="text-(--color-text-secondary) hover:text-(--color-text-primary) text-xs"
          >
            ← {t('monthDetail.backToCalendar')}
          </Link>
          <h1 className="text-lg sm:text-xl font-semibold text-(--color-text-primary)">
            {monthLabel(monthIso)}
          </h1>
          <RideAttribution text={data.data_sources?.join(', ')} />
          <p className="text-xs text-(--color-text-secondary)">
            {formatDateRange(data.month_start, data.month_end)}
          </p>
        </div>
        {!isEmpty && (
          <button
            type="button"
            onClick={() => generate.mutate(monthIso)}
            disabled={isGenerating}
            className="text-xs font-medium border border-emerald-400/40 hover:border-emerald-300/60 text-emerald-400 hover:text-emerald-300 disabled:opacity-50 rounded-full px-3 py-1.5 transition-colors"
          >
            {isGenerating ? t('monthDetail.regenerating') : generateLabel}
          </button>
        )}
      </div>

      {isEmpty && (
        <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-6 text-center text-(--color-text-secondary)">
          <p className="font-medium text-(--color-text-primary)">
            {t('monthDetail.restMonthTitle')}
          </p>
          <p className="text-sm mt-1">{t('monthDetail.restMonthBody')}</p>
        </div>
      )}

      {!isEmpty && <StatsPanel stats={stats} />}

      {!isEmpty && data.flags.length > 0 && (
        <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-2">
          <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
            {t('monthDetail.flagsTitle')}
          </h2>
          <div className="flex flex-wrap gap-2">
            {data.flags.map((f) => (
              <SafetyFlagPill key={f.id} flag={f} />
            ))}
          </div>
        </div>
      )}

      {!isEmpty && stats.weeks.length > 0 && (
        <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-2">
          <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
            {t('monthDetail.weeksTitle')}
          </h2>
          <ul className="flex flex-col gap-2">
            {stats.weeks.map((w) => (
              <li key={w.week_start}>
                <WeekRow week={w} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isEmpty && (
        <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-3">
          <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
            {t('monthDetail.analysisTitle')}
          </h2>
          {sections ? (
            <div className="flex flex-col gap-4">
              {sections.map((s, i) => (
                <div key={`${i}-${s.heading}`} className="flex flex-col gap-1">
                  {s.heading && (
                    <h3 className="text-base font-semibold text-(--color-text-primary)">
                      {s.heading}
                    </h3>
                  )}
                  <p className="text-sm text-(--color-text-primary) leading-relaxed whitespace-pre-wrap">
                    {s.body}
                  </p>
                </div>
              ))}
              <GarminAiAttribution sources={data.ai_sources} />
              {data.summary_at && (
                <p className="text-[10px] text-(--color-text-secondary)">
                  {t('monthDetail.generatedAt', {
                    when: new Date(data.summary_at).toLocaleString(),
                  })}
                  {data.prompt_version && ` · ${data.prompt_version}`}
                </p>
              )}
            </div>
          ) : (
            <div className="text-sm text-(--color-text-secondary)">
              {t('monthDetail.notGeneratedYet')}
            </div>
          )}
        </div>
      )}

      {comparison && (
        <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-2">
          <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
            {t('monthDetail.comparisonTitle')}
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-(--color-text-secondary) text-xs">
                <th className="text-left font-normal py-1"> </th>
                <th className="text-right font-normal py-1">
                  {t('monthDetail.thisMonth')}
                </th>
                <th className="text-right font-normal py-1">
                  {t('monthDetail.lastMonth')}
                </th>
                <th className="text-right font-normal py-1">Δ</th>
              </tr>
            </thead>
            <tbody>
              {comparison.map((row) => (
                <tr
                  key={row.label}
                  className="border-t border-(--color-border)/40"
                >
                  <td className="py-1.5 text-(--color-text-secondary)">
                    {row.label}
                  </td>
                  <td className="py-1.5 text-right font-medium text-(--color-text-primary)">
                    {row.fmtCur}
                  </td>
                  <td className="py-1.5 text-right text-(--color-text-secondary)">
                    {row.fmtPrev}
                  </td>
                  <td
                    className={`py-1.5 text-right font-medium ${
                      row.cur - row.prev >= 0
                        ? 'text-emerald-400'
                        : 'text-amber-400'
                    }`}
                  >
                    {row.fmtDelta}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatsPanel({ stats }: Readonly<{ stats: MonthlyStats }>) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-3">
      <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
        {t('monthDetail.statsTitle')}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <StatTile label={t('monthDetail.rides')} value={`${stats.totals.rides}`} />
        <StatTile label={t('monthDetail.hours')} value={`${stats.totals.hours.toFixed(1)}h`} />
        <StatTile label={t('monthDetail.distance')} value={`${stats.totals.km} km`} />
        <StatTile label="TSS" value={`${stats.totals.tss}`} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-sm">
        <StatTile
          label="CTL"
          value={`${stats.ctl_start} → ${stats.ctl_end}`}
          delta={stats.ctl_end - stats.ctl_start}
        />
        <StatTile
          label="ATL"
          value={`${stats.atl_start} → ${stats.atl_end}`}
          delta={stats.atl_end - stats.atl_start}
        />
        <StatTile label="TSB end" value={`${stats.tsb_end}`} />
      </div>
      {stats.ctl_trajectory.length >= 2 && (
        <div>
          <p className="text-xs text-(--color-text-secondary) mb-1">
            {t('monthDetail.ctlTrajectory')}
          </p>
          <p className="font-mono text-xs text-(--color-text-primary)">
            {stats.ctl_trajectory.join(' → ')}
          </p>
        </div>
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  delta,
}: Readonly<{ label: string; value: string; delta?: number }>) {
  return (
    <div className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) p-2">
      <div className="text-[10px] text-(--color-text-secondary) uppercase tracking-wide">
        {label}
      </div>
      <div className="text-(--color-text-primary) font-semibold text-sm">
        {value}
      </div>
      {delta !== undefined && (
        <div
          className={`text-[10px] ${
            delta >= 0 ? 'text-emerald-400' : 'text-amber-400'
          }`}
        >
          Δ {delta >= 0 ? '+' : ''}
          {Math.round(delta * 10) / 10}
        </div>
      )}
    </div>
  );
}

function WeekRow({ week }: Readonly<{ week: MonthlyWeekBlock }>) {
  return (
    <Link
      to={`/weeks/${week.week_start}`}
      className="flex items-center justify-between gap-3 rounded-lg bg-(--color-bg-tertiary) border border-(--color-border)/60 hover:border-emerald-400/40 px-3 py-2 text-xs transition-colors"
    >
      <div className="flex flex-col min-w-0">
        <span className="text-(--color-text-primary) font-medium">
          {week.week_start} → {week.week_end}
        </span>
        <span className="text-(--color-text-secondary)">
          {week.totals.rides} rides · {week.totals.hours.toFixed(1)}h ·{' '}
          {week.totals.km} km · TSS {week.totals.tss}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-(--color-text-secondary) text-[10px]">
          CTL {week.ctl_end} · TSB {week.tsb_end}
        </span>
        {week.flags.length > 0 && (
          <span className="rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 px-2 py-0.5 text-[10px]">
            {week.flags.length} flag{week.flags.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
    </Link>
  );
}
